/**
 * Erfassungs-Maske für Fahrzeuge — Tabs: Fahrzeug, Zuordnung, Vertrag, Termine, Tankkarte, Reifen, Notizen.
 */
import React, { useState, useEffect } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../../components/ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { Button } from '../../components/ui/button';
import { Switch } from '../../components/ui/switch';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Loader2, Save, Car, Users, FileText, Calendar, CreditCard, Snowflake, NotebookPen } from 'lucide-react';
import { toast } from 'sonner';
import { vehiclesAPI } from '../../lib/api';
import { DatePickerDE } from '../../components/ui/date-picker-de';
import { formatLastFirst, getDuplicateNameKeys } from '../../lib/nameFormat';

const EMPTY = {
  license_plate: '', brand: '', model: '', variant: '', vin: '', first_registration: '',
  fuel_type: '', color: '',
  holder_employee_id: '', branch_id: '', location_id: '', usage: '', status: 'active',
  leasing: false, lessor: '', contract_start: '', contract_end: '', monthly_rate: '',
  insurance_company: '', insurance_policy_no: '', insurance_deductible: '', current_km: '',
  tuv_due: '', inspection_due: '', tire_change_due: '',
  fuel_card_provider: '', fuel_card_number_masked: '', fuel_card_limit: '',
  current_tires: '', tire_storage_location: '', tire_storage_notes: '',
  notes: '',
};

export const VehicleDialog = ({ open, onOpenChange, vehicle, employees, branches, locations, onSaved }) => {
  const [form, setForm] = useState(EMPTY);
  const [saving, setSaving] = useState(false);
  const isEdit = !!vehicle;

  useEffect(() => {
    if (vehicle) {
      setForm({ ...EMPTY, ...vehicle, ...Object.fromEntries(Object.keys(EMPTY).map(k => [k, vehicle[k] ?? EMPTY[k]])) });
    } else {
      setForm(EMPTY);
    }
  }, [vehicle, open]);

  const set = (k, v) => setForm(prev => ({ ...prev, [k]: v }));

  const handleSave = async () => {
    if (!form.license_plate?.trim()) {
      toast.error('Kennzeichen ist Pflicht');
      return;
    }
    setSaving(true);
    try {
      const payload = { ...form };
      // Numerische Felder konvertieren
      ['monthly_rate', 'insurance_deductible', 'current_km', 'fuel_card_limit'].forEach(k => {
        if (payload[k] === '' || payload[k] === null) payload[k] = null;
        else if (typeof payload[k] === 'string') payload[k] = parseFloat(payload[k]) || null;
      });
      // Leere Strings → null
      Object.keys(payload).forEach(k => {
        if (payload[k] === '') payload[k] = null;
      });
      if (isEdit) {
        await vehiclesAPI.update(vehicle.id, payload);
        toast.success('Fahrzeug aktualisiert');
      } else {
        await vehiclesAPI.create(payload);
        toast.success('Fahrzeug angelegt');
      }
      onOpenChange(false);
      onSaved?.();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Fehler beim Speichern');
    } finally {
      setSaving(false);
    }
  };

  const filteredLocations = form.branch_id
    ? locations.filter(l => l.branch_id === form.branch_id)
    : locations;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>{isEdit ? `Fahrzeug bearbeiten: ${vehicle.license_plate}` : 'Neues Fahrzeug'}</DialogTitle>
        </DialogHeader>

        <Tabs defaultValue="basics">
          <div className="overflow-x-auto scrollbar-hide -mx-6 px-6">
            <TabsList className="inline-flex w-max">
              <TabsTrigger value="basics" className="whitespace-nowrap"><Car className="h-4 w-4 mr-1" />Fahrzeug</TabsTrigger>
              <TabsTrigger value="assign" className="whitespace-nowrap"><Users className="h-4 w-4 mr-1" />Zuordnung</TabsTrigger>
              <TabsTrigger value="contract" className="whitespace-nowrap"><FileText className="h-4 w-4 mr-1" />Vertrag</TabsTrigger>
              <TabsTrigger value="dates" className="whitespace-nowrap"><Calendar className="h-4 w-4 mr-1" />Termine</TabsTrigger>
              <TabsTrigger value="card" className="whitespace-nowrap"><CreditCard className="h-4 w-4 mr-1" />Tankkarte</TabsTrigger>
              <TabsTrigger value="tires" className="whitespace-nowrap"><Snowflake className="h-4 w-4 mr-1" />Reifen</TabsTrigger>
              <TabsTrigger value="notes" className="whitespace-nowrap"><NotebookPen className="h-4 w-4 mr-1" />Notizen</TabsTrigger>
            </TabsList>
          </div>

          <TabsContent value="basics" className="grid gap-4 sm:grid-cols-2 mt-4">
            <Field label="Kennzeichen *"><Input value={form.license_plate} onChange={e => set('license_plate', e.target.value.toUpperCase())} data-testid="field-license-plate" /></Field>
            <Field label="Erstzulassung"><DatePickerDE value={form.first_registration} onChange={v => set('first_registration', v)} placeholder="Erstzulassung" /></Field>
            <Field label="Marke"><Input value={form.brand} onChange={e => set('brand', e.target.value)} /></Field>
            <Field label="Modell"><Input value={form.model} onChange={e => set('model', e.target.value)} /></Field>
            <Field label="Variante"><Input value={form.variant} onChange={e => set('variant', e.target.value)} /></Field>
            <Field label="FIN / Fahrgestellnr"><Input value={form.vin} onChange={e => set('vin', e.target.value)} /></Field>
            <Field label="Treibstoff">
              <Select value={form.fuel_type} onValueChange={v => set('fuel_type', v)}>
                <SelectTrigger><SelectValue placeholder="—" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="petrol">Benzin</SelectItem>
                  <SelectItem value="diesel">Diesel</SelectItem>
                  <SelectItem value="hybrid">Hybrid</SelectItem>
                  <SelectItem value="electric">Elektro</SelectItem>
                  <SelectItem value="lpg">Autogas (LPG)</SelectItem>
                </SelectContent>
              </Select>
            </Field>
            <Field label="Farbe"><Input value={form.color} onChange={e => set('color', e.target.value)} /></Field>
          </TabsContent>

          <TabsContent value="assign" className="grid gap-4 sm:grid-cols-2 mt-4">
            <Field label="Halter (Mitarbeiter)">
              <Select value={form.holder_employee_id || 'none'} onValueChange={v => set('holder_employee_id', v === 'none' ? '' : v)}>
                <SelectTrigger><SelectValue placeholder="Kein Halter" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">— kein Halter —</SelectItem>
                  {(() => {
                    const list = employees.filter(e => e.is_active !== false);
                    const dk = getDuplicateNameKeys(list);
                    return list.map(e => (
                      <SelectItem key={e.id} value={e.id}>{formatLastFirst(e, dk)}</SelectItem>
                    ));
                  })()}
                </SelectContent>
              </Select>
            </Field>
            <Field label="Niederlassung">
              <Select value={form.branch_id || 'none'} onValueChange={v => { set('branch_id', v === 'none' ? '' : v); set('location_id', ''); }}>
                <SelectTrigger><SelectValue placeholder="—" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">—</SelectItem>
                  {branches.map(b => <SelectItem key={b.id} value={b.id}>{b.name}</SelectItem>)}
                </SelectContent>
              </Select>
            </Field>
            <Field label="Standort">
              <Select value={form.location_id || 'none'} onValueChange={v => set('location_id', v === 'none' ? '' : v)}>
                <SelectTrigger><SelectValue placeholder="—" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">—</SelectItem>
                  {filteredLocations.map(l => <SelectItem key={l.id} value={l.id}>{l.name}</SelectItem>)}
                </SelectContent>
              </Select>
            </Field>
            <Field label="Verwendung">
              <Select value={form.usage || 'none'} onValueChange={v => set('usage', v === 'none' ? '' : v)}>
                <SelectTrigger><SelectValue placeholder="—" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">—</SelectItem>
                  <SelectItem value="company_car">Dienstwagen</SelectItem>
                  <SelectItem value="pool">Poolfahrzeug</SelectItem>
                  <SelectItem value="service">Werkstatt-/Servicewagen</SelectItem>
                </SelectContent>
              </Select>
            </Field>
            <Field label="Status">
              <Select value={form.status} onValueChange={v => set('status', v)}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="active">Aktiv</SelectItem>
                  <SelectItem value="workshop">Werkstatt</SelectItem>
                  <SelectItem value="sold">Verkauft</SelectItem>
                  <SelectItem value="decommissioned">Stillgelegt</SelectItem>
                </SelectContent>
              </Select>
            </Field>
            <Field label="Kilometerstand"><Input type="number" value={form.current_km} onChange={e => set('current_km', e.target.value)} /></Field>
          </TabsContent>

          <TabsContent value="contract" className="grid gap-4 sm:grid-cols-2 mt-4">
            <div className="sm:col-span-2 flex items-center gap-2">
              <Switch checked={form.leasing} onCheckedChange={v => set('leasing', v)} id="leasing-switch" />
              <Label htmlFor="leasing-switch">Leasingfahrzeug</Label>
            </div>
            <Field label="Leasinggeber"><Input value={form.lessor} onChange={e => set('lessor', e.target.value)} /></Field>
            <Field label="Monatliche Rate (€)"><Input type="number" step="0.01" value={form.monthly_rate} onChange={e => set('monthly_rate', e.target.value)} /></Field>
            <Field label="Vertragsbeginn"><DatePickerDE value={form.contract_start} onChange={v => set('contract_start', v)} placeholder="Vertragsbeginn" /></Field>
            <Field label="Vertragsende"><DatePickerDE value={form.contract_end} onChange={v => set('contract_end', v)} placeholder="Vertragsende" /></Field>
            <Field label="Versicherung"><Input value={form.insurance_company} onChange={e => set('insurance_company', e.target.value)} /></Field>
            <Field label="Police-Nr."><Input value={form.insurance_policy_no} onChange={e => set('insurance_policy_no', e.target.value)} /></Field>
            <Field label="Selbstbeteiligung (€)"><Input type="number" step="0.01" value={form.insurance_deductible} onChange={e => set('insurance_deductible', e.target.value)} /></Field>
          </TabsContent>

          <TabsContent value="dates" className="grid gap-4 sm:grid-cols-2 mt-4">
            <Field label="TÜV / HU fällig"><DatePickerDE value={form.tuv_due} onChange={v => set('tuv_due', v)} placeholder="TÜV-Datum" /></Field>
            <Field label="Inspektion fällig"><DatePickerDE value={form.inspection_due} onChange={v => set('inspection_due', v)} placeholder="Inspektion" /></Field>
            <Field label="Reifenwechsel fällig"><DatePickerDE value={form.tire_change_due} onChange={v => set('tire_change_due', v)} placeholder="Reifenwechsel" /></Field>
          </TabsContent>

          <TabsContent value="card" className="grid gap-4 sm:grid-cols-2 mt-4">
            <Field label="Anbieter">
              <Select value={form.fuel_card_provider || 'none'} onValueChange={v => set('fuel_card_provider', v === 'none' ? '' : v)}>
                <SelectTrigger><SelectValue placeholder="—" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">— keine —</SelectItem>
                  <SelectItem value="Shell">Shell</SelectItem>
                  <SelectItem value="Aral">Aral / BP</SelectItem>
                  <SelectItem value="DKV">DKV</SelectItem>
                  <SelectItem value="UTA">UTA</SelectItem>
                  <SelectItem value="Total">Total Energies</SelectItem>
                  <SelectItem value="other">Sonstige</SelectItem>
                </SelectContent>
              </Select>
            </Field>
            <Field label="Kartennummer (verkürzt)"><Input value={form.fuel_card_number_masked} onChange={e => set('fuel_card_number_masked', e.target.value)} placeholder="•••• 1234" /></Field>
            <Field label="Limit pro Monat (€)"><Input type="number" value={form.fuel_card_limit} onChange={e => set('fuel_card_limit', e.target.value)} /></Field>
          </TabsContent>

          <TabsContent value="tires" className="grid gap-4 sm:grid-cols-2 mt-4">
            <Field label="Aktuell montiert">
              <Select value={form.current_tires || 'none'} onValueChange={v => set('current_tires', v === 'none' ? '' : v)}>
                <SelectTrigger><SelectValue placeholder="—" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">—</SelectItem>
                  <SelectItem value="summer">Sommer</SelectItem>
                  <SelectItem value="winter">Winter</SelectItem>
                  <SelectItem value="allseason">Ganzjahr</SelectItem>
                </SelectContent>
              </Select>
            </Field>
            <div className="sm:col-span-2">
              <Field label="Lagerort der Wechselreifen">
                <Input
                  value={form.tire_storage_location}
                  onChange={e => set('tire_storage_location', e.target.value)}
                  placeholder="z.B. Reifen Müller GmbH, Erfurt-Nord oder Halle 2, Standort Leipzig"
                  data-testid="field-tire-storage"
                />
              </Field>
            </div>
            <div className="sm:col-span-2">
              <Field label="Notizen zu Reifen / Lagerung">
                <Textarea
                  value={form.tire_storage_notes}
                  onChange={e => set('tire_storage_notes', e.target.value)}
                  placeholder="z.B. Profil-Tiefe, Größe, Zustand, Lager-Nr..."
                  rows={3}
                />
              </Field>
            </div>
          </TabsContent>

          <TabsContent value="notes" className="mt-4">
            <Field label="Allgemeine Bemerkungen">
              <Textarea value={form.notes} onChange={e => set('notes', e.target.value)} rows={6} placeholder="Sonstige Hinweise zum Fahrzeug..." />
            </Field>
          </TabsContent>
        </Tabs>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={saving}>Abbrechen</Button>
          <Button onClick={handleSave} disabled={saving} data-testid="btn-save-vehicle">
            {saving ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Save className="h-4 w-4 mr-2" />}
            {isEdit ? 'Speichern' : 'Anlegen'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

const Field = ({ label, children }) => (
  <div className="space-y-1.5">
    <Label className="text-xs text-muted-foreground">{label}</Label>
    {children}
  </div>
);
